// @ts-check
// settings-panel.js — Settings as a side-panel sibling to Notes / Tasks /
// Schedule.
//
// Lives inside #panel-lists as #settings-container. Unlike the other three
// panels it isn't part of the cycle: the workspace avatar button (or the
// Cmd/Ctrl+, shortcut) opens it as a temporary overlay over whichever
// panel was active. Closing restores the previous panel.
//
// Layout: search input at the top (styled like #searchBox), then a single
// flat list of <li> rows underneath. Rows are bare text — no decorative
// icons — with controls inline on the right or sub-rows revealed below
// when a row is expanded. Clicking the "Settings" header closes the panel
// (alongside panels.cyclePanel, the workspace avatar, and Cmd/Ctrl+,).

import {
  closeMobilePanel,
  filesContainer,
  mobileMediaQuery,
  panelLists,
  scheduleContainer,
  searchContainer,
  settingsContainer,
  todosContainer,
} from '../core/app-state.js';
import { PreviewRenderer } from '../editor/markdown-renderer.js';
import { subscribeNotesChange } from '../storage/notes-broadcast.js';
import { getSyncHelpers } from '../storage/session.js';
import { createKeyedList } from '../ui/keyed-list.js';
import { reinitMermaidTheme } from '../ui/mermaid-theme.js';
import { getIsPanelPinned } from '../ui/panels.js';
import { onThemeChange } from '../ui/theme-engine.js';
import { createSearchPredicate } from '../utils/search-predicate.js';
import { SettingsRouter } from './routing.js';
import { renderAppearance } from './sections/appearance.js';
import { renderBookmarklets } from './sections/bookmarklets.js';
import { renderCalendars } from './sections/calendars.js';
import { handleEncryptionReady, primeDeviceId, renderEncryption } from './sections/encryption.js';
import { renderFilters } from './sections/filters.js';
import { handleAuthStateChange, renderSync } from './sections/sync.js';
import { clearMembersCache, renderWorkspaces } from './sections/workspaces.js';
import { descsToCells, indentDepth } from './settings-reconcile.js';
import type {
  RawRowDraft,
  RowDescriptor,
  RowDraft,
  SettingsRawRowOpts,
  SettingsRowOpts,
  SettingsSectionCtx,
  TemplatedRowDraft,
} from './settings-section-api.js';
import { clearPending, invalidate, onReconcile, takeDirty } from './settings-state.js';

// Panel control surface, assigned at the end of the IIFE below and exported
// as the ESM `SettingsPanel` binding. Declared at module scope so the export
// can reference it after the (synchronous) IIFE populates it.
/** @type {any} */
let _SettingsPanel: any;

(() => {
  // Expandable rows that are open right now. Keyed by row id so re-render
  // preserves state. _renderAll only includes sub-rows when the parent id
  // is in this set. Persisted to localStorage (_persistExpansions) and restored
  // here, so each settings heading's collapsed/expanded state survives a reload.
  const _EXPANSIONS_KEY = 'settings_expanded_v1';
  function _loadExpansions(): string[] {
    try {
      const arr = JSON.parse(localStorage.getItem(_EXPANSIONS_KEY) || '[]');
      return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
    } catch {
      return [];
    }
  }
  const _openExpansions = new Set<string>(_loadExpansions());
  // Persist the user's expansions whenever they change. Called from _reconcile
  // (the single render chokepoint every expansion change funnels through), so it
  // catches every mutation path — the fold-click (_toggleExpansion), a section
  // heading's own onActivate, and the openExpansion/closeExpansion API — without
  // having to instrument each one. The change-guard keeps it to one write per
  // actual change despite running on every render. `_openExpansions` is the raw
  // user set (search auto-expand lives in _searchOpenIds), so search never
  // pollutes the persisted state.
  let _lastPersisted = JSON.stringify([..._openExpansions]);
  function _persistExpansions(): void {
    try {
      const s = JSON.stringify([..._openExpansions]);
      if (s === _lastPersisted) return;
      _lastPersisted = s;
      localStorage.setItem(_EXPANSIONS_KEY, s);
    } catch {
      /* storage unavailable — non-fatal */
    }
  }
  let _prevPanelId = 'files';
  let _opened = false;
  /** @type {any} */
  let _listEl: any = null;
  /** @type {any} */
  let _searchEl: any = null;
  /** Host-owned no-results message — a sibling of the list, NOT a list child,
   * so the keyed reconciler fully owns #settingsList. @type {any} */
  let _hintEl: any = null;
  let _query = '';

  // The keyed-list reconciler bound to #settingsList. Built in _wire once the
  // element exists; reuses row nodes in place across renders (see
  // settings-reconcile / ui/keyed-list).
  /** @type {any} */
  let _keyedList: any = null;

  // Group ids that opted into search auto-expand via `searchExpand: true` on
  // their row. Repopulated from scratch on every full render (a row registers
  // itself in _row). Consumed only while a search is active — see _isOpen.
  const _searchOpenIds = new Set<string>();

  /** @returns {boolean} whether a non-empty search query is active. */
  function _searchActive() {
    return !!(_query || '').trim();
  }

  // Effective open-state for an expandable group: the user's own expansion,
  // OR — during an active search — a group that opted into search auto-expand.
  // This is the single source of truth for both "should the section emit this
  // group's sub-rows" (via the _expansionView proxy below) and the row's
  // open/aria-expanded chrome, so the chevron matches what's rendered.
  // Crucially it does NOT force open lazy/side-effectful nested groups
  // (calendar accounts, device lists) — those never set searchExpand, so a
  // keystroke never triggers their network/DB loads.
  /**
   * @param {any} id
   * @returns {boolean}
   */
  function _isOpen(id: any) {
    return _openExpansions.has(id) || (_searchActive() && _searchOpenIds.has(id));
  }

  // Sections gate sub-row emission on `ctx.openExpansions.has(id)`. We hand
  // them this proxy instead of the raw Set so `.has()` reflects the effective
  // (search-aware) open-state, while `.add()`/`.delete()` still mutate the
  // real _openExpansions — the user's manual expansions are preserved and
  // restored verbatim when the search clears.
  const _expansionView: Set<string> = new Proxy(_openExpansions, {
    get(target: any, prop: any) {
      if (prop === 'has') return (id: any) => _isOpen(id);
      const v = target[prop];
      return typeof v === 'function' ? v.bind(target) : v;
    },
  }) as any;

  // ── Panel open / close ─────────────────────────────────────────────────
  /** @returns {string} the currently-active panel id. */
  function _activePanelId() {
    if (filesContainer.classList.contains('active')) return 'files';
    if (todosContainer.classList.contains('active')) return 'tasks';
    if (scheduleContainer.classList.contains('active')) return 'schedule';
    if (searchContainer.classList.contains('active')) return 'search';
    if (settingsContainer.classList.contains('active')) return 'settings';
    return 'files';
  }

  /**
   * @param {string} id
   * @returns {any} the panel container element, or undefined.
   */
  function _panelEl(id: any) {
    return (
      {
        files: filesContainer,
        tasks: todosContainer,
        schedule: scheduleContainer,
        search: searchContainer,
        settings: settingsContainer,
      } as any
    )[id];
  }

  /** @returns {void} */
  function _open() {
    if (_opened) return;
    _opened = true;
    const cur = _activePanelId();
    if (cur !== 'settings') _prevPanelId = cur;
    for (const id of ['files', 'tasks', 'schedule', 'search', 'settings']) {
      _panelEl(id)?.classList.toggle('active', id === 'settings');
    }
    panelLists.classList.add('visible');
    document.body.classList.add('panel-visible');
    if (mobileMediaQuery?.matches) {
      panelLists.classList.add('mobile-open-right');
      const overlay = document.getElementById('mobile-overlay');
      overlay?.classList.add('active');
    }
    _renderAll();
    _searchEl?.focus();
  }

  /** @returns {void} */
  function _close() {
    if (!_opened) return;
    _opened = false;
    for (const id of ['files', 'tasks', 'schedule', 'search', 'settings']) {
      _panelEl(id)?.classList.toggle('active', id === _prevPanelId);
    }
    if (!getIsPanelPinned()) {
      panelLists.classList.remove('visible');
      document.body.classList.remove('panel-visible');
    }
    closeMobilePanel();
  }

  // ── Row primitives ────────────────────────────────────────────────────
  // Toggle one expandable group's open-state by id, then re-render. The single
  // fold mechanism behind the editor-style affordances: a collapsed group's
  // fold-squiggle (expand) and an open group's vertical accent line (collapse)
  // both route here. Heading text is deliberately NOT a fold target — mirroring
  // the editor, where the heading stays a caret target and only the line /
  // squiggle fold. Keyboard Enter/Space on a focused group row also calls this.
  function _toggleExpansion(id: any) {
    if (id == null) return;
    if (_openExpansions.has(id)) _openExpansions.delete(id);
    else _openExpansions.add(id);
    _renderAll(); // _renderAll → _reconcile persists the new state
  }

  /** A row's indent depth (0–3) from its `settings-row-indent*` classes. */
  function _indentLevelOf(el: any): number {
    const cl = el.classList;
    if (!cl) return 0;
    if (cl.contains('settings-row-indent-3')) return 3;
    if (cl.contains('settings-row-indent-2')) return 2;
    if (cl.contains('settings-row-indent')) return 1;
    return 0;
  }

  // The settings analogue of the editor's `_foldSlotAt` (click-handlers.cm.ts):
  // given row `li` and a pointer x, return the nearest vertical-line slot under
  // the cursor (outermost = 0), or -1 if the cursor is outside the fold gutter.
  // Identical logic to the editor — the whole indent gutter is accepted (up to
  // where the text starts) plus a left GRAB margin (wider on coarse/narrow), and
  // the cursor snaps to the nearest line — so "what highlights" always equals
  // "what a click folds". Settings columns are 20px with lines at 8 + i*20.
  const _LINE_GAP = 20;
  const _LINE_X0 = 8;
  // Hoisted MediaQueryList objects — created once, not re-parsed per mousemove.
  // `.matches` stays live, so reading it later reflects the current viewport.
  const _mqCoarse = typeof matchMedia === 'function' ? matchMedia('(pointer: coarse)') : null;
  const _mqNarrow = typeof matchMedia === 'function' ? matchMedia('(max-width: 650px)') : null;
  function _foldSlotAtRow(li: any, e: MouseEvent): number {
    const L = _indentLevelOf(li);
    if (L <= 0) return -1;
    const rect = li.getBoundingClientRect();
    const x = (e.clientX || 0) - rect.left;
    const coarse = !!(_mqCoarse?.matches || _mqNarrow?.matches);
    const grab = coarse ? 18 : 8;
    // Text starts at the indent padding (20*L + 4); the lines sit within it. The
    // right edge stops AT the text start so the row content stays clickable.
    const textStart = _LINE_GAP * L + 4;
    if (x < _LINE_X0 - grab || x > textStart) return -1;
    let slot = 0;
    let best = Math.abs(x - _LINE_X0);
    for (let i = 1; i < L; i++) {
      const d = Math.abs(x - (_LINE_X0 + i * _LINE_GAP));
      if (d < best) {
        best = d;
        slot = i;
      }
    }
    return slot;
  }

  // Resolve the group id whose vertical accent line was clicked in row `li`'s
  // gutter, or null if the click wasn't on a line. Shares the hit-test with the
  // hover path, then maps the slot → its owning group's fold index (stamped on
  // the row by _annotateLines) → the group id.
  function _groupForLineClick(li: any, e: MouseEvent): string | null {
    const slot = _foldSlotAtRow(li, e);
    if (slot < 0) return null;
    const idx = (li.dataset.lineSec || '').split(',')[slot];
    const n = idx === '' || idx == null ? NaN : Number(idx);
    return Number.isNaN(n) ? null : (_idxToId[n] ?? null);
  }

  // Describe a single row as DATA. The reconciler (settings-reconcile) builds
  // the <li> once and patches it in place; this function is pure — no DOM,
  // open-state baked in from current state at build time. Decorative icons are
  // gone app-wide; expandable rows carry no chevron and the heading itself
  // doesn't toggle — the fold-squiggle (collapsed) and the vertical accent line
  // down the children (open) are the click targets, like the editor. The only
  // optional glyph is the trailing "⋯" aux button on dual-action rows.
  function _row(opts: SettingsRowOpts): TemplatedRowDraft {
    // A group that opts into search auto-expand registers its id so an active
    // search reveals its sub-rows (see _isOpen). Done at build time, before the
    // section's own `has(id)` gate check, so the flag is in place by then.
    if (opts.searchExpand && opts.id) _searchOpenIds.add(opts.id);

    // Body activation is ONLY a section-supplied onClick (e.g. workspaces
    // "switch"). An expandable row with no onClick has no body activation — its
    // fold is driven by the squiggle / line clicks the host's delegated listener
    // resolves, not by clicking the heading.
    const onActivate: ((e: Event) => void) | undefined = opts.onClick;

    return {
      kind: 'row',
      key: opts.key,
      label: opts.label,
      id: opts.id,
      cls: opts.cls,
      searchText: (opts.searchText || `${opts.label} ${opts.value ?? ''}`).toLowerCase(),
      value: opts.value,
      indent: opts.indent,
      expandable: opts.expandable,
      active: opts.active,
      open: opts.expandable ? _isOpen(opts.id) : undefined,
      control: opts.control,
      updateControl: opts.updateControl,
      onActivate,
      onAux: opts.onAux,
      auxLabel: opts.auxLabel,
      editable: opts.editable,
    };
  }

  // Describe a free-form container row (forms, device lists). Bypasses the
  // label/control template so callers can nest arbitrary DOM beneath an
  // expanded parent.
  function _rawRow(content: (() => Node) | null, opts: SettingsRawRowOpts = {}): RawRowDraft {
    return {
      kind: 'raw',
      key: opts.key,
      searchText: (opts.searchText || '').toLowerCase(),
      cls: opts.cls,
      indent: opts.indent,
      content,
      update: opts.update,
    };
  }

  // Repaint chain — runs on every applyTheme, same-tab or cross-tab. Mermaid
  // diagrams bake the palette into rendered SVG; preview rendering bakes code-block token colours; the settings
  // panel's swatch grid needs to update which swatch shows the selected ring.
  // Registered once at module init and never unsubscribed (settings-panel
  // lives for the page's lifetime).
  onThemeChange(() => {
    reinitMermaidTheme();
    PreviewRenderer?.rerenderForThemeChange?.();
    // The panel's only theme-dependent surface is the Appearance section's
    // swatch grid (selected-ring) + collapsed two-dot summary. _renderAll
    // reconciles, so every other section's rows are retained by identity —
    // only the appearance rows whose content changed are patched in place.
    // Skip the rebuild while the panel is closed — _open() runs a full
    // _renderAll() on reopen, so the swatch grid still picks up the new theme.
    if (_opened) _renderAll();
  });

  // ── Render orchestrator ───────────────────────────────────────────────
  // Section registry, rendered top-to-bottom. Each is renderX(ctx) → rows[].
  // Adding a section means one import + one entry here.
  // The standalone single-file build (web/scripts/build-standalone.mjs) strips
  // all syncing, so the workspace / sync / encryption / calendar sections have
  // nothing to configure. That build sets a truthy `__ARIADNE_NO_SYNC__` global
  // before the bundle runs; here we only READ it (the assignment lives in the
  // generated HTML, never in web/js/**, so the window.X ratchet is untouched) and
  // drop those four sections, leaving only Appearance + Filters. Undefined in the
  // normal web/Electron/iOS build and under test, so the full list renders there.
  const _NO_SYNC = !!(globalThis as any)['__ARIADNE_NO_SYNC__'];
  const _SECTIONS = _NO_SYNC
    ? [renderAppearance, renderFilters]
    : [
        renderWorkspaces,
        renderSync,
        renderEncryption,
        renderCalendars,
        renderAppearance,
        renderFilters,
        renderBookmarklets,
      ];

  // Per-section ctx, cached + stable. Each section's `invalidate()` marks only
  // that section dirty, so a change in one section never re-runs another's
  // (side-effectful) build. The other primitives are shared.
  const _sectionCtxs: SettingsSectionCtx[] = [];
  function _ctxFor(i: number): SettingsSectionCtx {
    let c = _sectionCtxs[i];
    if (!c) {
      c = {
        row: _row,
        rawRow: _rawRow,
        openExpansions: _expansionView,
        invalidate: () => invalidate(i),
        isOpened: () => _opened,
      };
      _sectionCtxs[i] = c;
    }
    return c;
  }

  // Cached descriptors per section index — rebuilt fully by _renderAll, only
  // for dirty sections by the batched _flushInvalidations.
  let _sectionDescs: RowDescriptor[][] = [];

  // Build one section's descriptors, tagging each with its section index + a
  // stable reconcile key: a section-supplied explicit key (state-machine forms),
  // else the row's id (`id:<id>` — stable for headings/accounts/devices), else a
  // positional `pos:<section>:<index>` fallback. The section's searchExpand ids
  // re-register here (see _row); idempotent, so a partial rebuild is fine.
  function _buildSection(i: number): RowDescriptor[] {
    const drafts = (_SECTIONS[i](_ctxFor(i)) || []) as RowDraft[];
    return drafts.map((d, pos): RowDescriptor => {
      const id = d.kind === 'row' ? d.id : undefined;
      const key = d.key || (id ? `id:${id}` : `pos:${i}:${pos}`);
      return { ...d, section: i, key } as RowDescriptor;
    });
  }

  // ── Section-line fold indices (editor parity) ──────────────────────────
  // Each expandable group gets a stable fold index, the settings analogue of
  // the editor's per-section indices. Its vertical line paints with
  // `var(--accent-line-sec-<idx>, faint)`, and hovering that one line sets the
  // matching `--accent-line-sec-<idx>` to bright on #settingsList — so only the
  // hovered section brightens, exactly like the editor. Indices are keyed by
  // group id so a group keeps its index (and its hover var) across re-renders.
  const _groupIdx = new Map<string, number>();
  const _idxToId: string[] = [];
  function _indexForGroup(id: string): number {
    let idx = _groupIdx.get(id);
    if (idx == null) {
      idx = _idxToId.length;
      _groupIdx.set(id, idx);
      _idxToId.push(id);
    }
    return idx;
  }
  // Walk the flattened rows top-to-bottom, reconstructing the open-group tree
  // from indent depth, and stamp each row with the fold index owning each of its
  // vertical-line slots (+ each expandable row's own fold index for its
  // squiggle). `groupAtLevel[i]` is the expandable group currently enclosing
  // rows at indent level i.
  function _annotateLines(descs: RowDescriptor[]) {
    const groupAtLevel: number[] = [];
    for (const d of descs) {
      const L = indentDepth(d.indent);
      groupAtLevel.length = L; // drop stale deeper-subtree entries
      d.lineSectionIds = L > 0 ? groupAtLevel.slice(0, L) : undefined;
      if (d.kind === 'row' && d.expandable && d.id != null) {
        const idx = _indexForGroup(d.id);
        groupAtLevel[L] = idx;
        d.foldSectionIdx = idx;
      } else {
        d.foldSectionIdx = undefined;
      }
    }
  }

  // Flatten the cached descriptors and reconcile them against the mounted nodes,
  // so a row whose description didn't change keeps its <li> (identity, focus, an
  // open native picker, :hover) instead of being rebuilt and swapped.
  function _reconcile() {
    if (!_listEl || !_keyedList) return;
    const flat = _sectionDescs.flat();
    _annotateLines(flat);
    _keyedList.render(descsToCells(flat));
    _applyFilter();
    // Save the (possibly just-changed) expansion state — robust across every
    // toggle path since all renders funnel through here. No-op when unchanged.
    _persistExpansions();
  }

  // Full synchronous render — rebuilds every section. Used by open/toggle/search
  // and the cross-cutting host events (theme repaint, auth-state, workspace
  // switch). _searchOpenIds is rebuilt from scratch since every section runs.
  function _renderAll() {
    if (!_listEl || !_keyedList) return;
    _searchOpenIds.clear();
    _sectionDescs = _SECTIONS.map((_unused, i) => _buildSection(i));
    clearPending();
    _reconcile();
  }

  // Batched partial render — fired (via the microtask scheduler) by section
  // `invalidate()`s. Rebuilds only the dirty sections, reusing every other
  // section's cached descriptors, so an unrelated section's side-effectful build
  // never re-runs. Falls back to a full render if the cache is cold or a
  // whole-panel invalidation was requested.
  function _flushInvalidations() {
    if (!_listEl || !_keyedList) return;
    const { all, sections } = takeDirty();
    if (all || _sectionDescs.length !== _SECTIONS.length) {
      _renderAll();
      return;
    }
    // Nothing dirty — a microtask left over after a synchronous _renderAll
    // already satisfied it. Skip the redundant reconcile pass.
    if (sections.size === 0) return;
    for (const i of sections) _sectionDescs[i] = _buildSection(i);
    _reconcile();
  }

  /** @returns {void} */
  function _applyFilter() {
    const q = (_query || '').trim().toLowerCase();
    if (!_listEl) return;
    // Mirror the Notes / Tasks search bars — boolean AND/OR/NOT plus
    // "exact phrase" via SearchPredicate. Bare queries (no operator)
    // fall back to a single literal includes() across each row's
    // dataset.search haystack.
    let pred: any;
    if (!q) {
      pred = () => true;
    } else {
      pred = createSearchPredicate(q, (token) => {
        let t = token;
        if (t.startsWith('"') && t.endsWith('"') && t.length > 2) t = t.slice(1, -1);
        const needle = t.toLowerCase();
        return (hay) => (hay || '').includes(needle);
      });
    }
    const rows = Array.from<any>(_listEl.children);
    const hits = rows.map((li) => pred(li.dataset.search || li.textContent.toLowerCase()));
    const show = hits.slice();

    // Reveal passes (no-op for the empty query, where everything hits):
    //   1. UP — a matched sub-row drags its owning group header into view so a
    //      hit never appears headerless. The header is the nearest row at-or-
    //      above the hit that opted into search-expand (_searchOpenIds) — the
    //      same groups the search force-opened. Walking to the nearest such
    //      group (rather than the section's first row) picks the RIGHT header
    //      when one section hosts several groups (e.g. each workspace in
    //      Workspaces), and works when a sub-item renders at indent 0.
    //   2. DOWN — when a HEADING itself matches (but its children don't), reveal
    //      the rendered sub-rows beneath it: its whole contiguous deeper-indent
    //      run, up to the next row at its own level or shallower. This keeps a
    //      force-opened (chevron-down) group from showing an empty body, and
    //      means searching a section name surfaces that section's items.
    if (q) {
      const indentOf = _indentLevelOf;
      for (let i = 0; i < rows.length; i++) {
        if (!hits[i]) continue;
        // 1 — owning header above.
        for (let j = i; j >= 0; j--) {
          const rid = rows[j].dataset.rowId;
          if (rid && _searchOpenIds.has(rid)) {
            show[j] = true;
            break;
          }
        }
        // 2 — sub-rows below a matched heading. Gate on the heading's own LABEL,
        // NOT its dataset.search: a group's searchText aggregates its children's
        // text, so matching on that would re-reveal every sibling whenever any
        // one child matched (search "Save now" → all shortcuts). The label is
        // the heading's intrinsic name, so this fires only on a true heading hit.
        if (rows[i].classList.contains('settings-row-expandable')) {
          const labelText = (
            rows[i].querySelector('.settings-row-text')?.textContent || ''
          ).toLowerCase();
          if (pred(labelText)) {
            const lvl = indentOf(rows[i]);
            for (let j = i + 1; j < rows.length && indentOf(rows[j]) > lvl; j++) {
              show[j] = true;
            }
          }
        }
      }
    }

    let matched = 0;
    for (let i = 0; i < rows.length; i++) {
      rows[i].hidden = !show[i];
      if (hits[i]) matched++;
    }
    // Toggle the host-owned hint sibling (see _wire) rather than poking the list.
    if (_hintEl) {
      const empty = !!q && matched === 0;
      if (empty) _hintEl.textContent = `No settings match “${q}”.`;
      _hintEl.hidden = !empty;
    }
  }

  // ── Wiring ────────────────────────────────────────────────────────────
  /** @returns {void} */
  function _wire() {
    _listEl = document.getElementById('settingsList');
    _searchEl = document.getElementById('settingsSearchBox');
    if (!_listEl || !_searchEl) return;

    _keyedList = createKeyedList(_listEl);
    // The no-results hint is a host-owned sibling of the list, so the reconciler
    // owns #settingsList outright (no foreign node to strip/re-append).
    _hintEl = document.createElement('div');
    _hintEl.className = 'settings-row settings-row-hint';
    _hintEl.hidden = true;
    _listEl.insertAdjacentElement('afterend', _hintEl);
    // Section `invalidate()`s coalesce into one microtask-batched partial render.
    onReconcile(_flushInvalidations);

    // ── Delegated row activation ──────────────────────────────────────────
    // One click + one keydown on the <ul>, for the page's lifetime — no
    // per-row listeners, no re-binding on render, no stale closures. Each row's
    // current intent lives on the node (`_onActivate`/`_onAux`), refreshed by
    // the reconciler on patch.
    //
    // Fold mirrors the editor: the heading is NOT a fold target, only the
    // editor-style affordances are —
    //   • the fold-squiggle on a COLLAPSED group → expand,
    //   • the vertical accent line on an OPEN group's children → collapse
    //     (the line slot under the pointer names the owning group, like the
    //      editor's gutter hit-test).
    // The trailing "⋯" aux button is a sub-target on dual-action rows
    // (workspaces: body switches, aux toggles); otherwise a click defers to a
    // real inner control (input/button/label/link/contenteditable), or finally
    // the row's own onClick (workspaces "switch", action rows).
    _listEl.addEventListener('click', (e: Event) => {
      const t = e.target as HTMLElement;
      const li = t.closest('li.settings-row') as any;
      if (!li || !_listEl.contains(li)) return;
      if (li._onAux && t.closest('.settings-row-aux')) {
        li._onAux(e);
        return;
      }
      // Fold-squiggle (collapsed group) → expand this group.
      if (t.closest('.settings-fold-squiggle')) {
        _toggleExpansion(li.dataset.rowId);
        return;
      }
      // Vertical accent line in an indented row's gutter → collapse the group
      // owning that line slot.
      const lineGroup = _groupForLineClick(li, e as MouseEvent);
      if (lineGroup) {
        _toggleExpansion(lineGroup);
        return;
      }
      if (t !== li && t.closest('input, button, label, a, [contenteditable]')) return;
      li._onActivate?.(e);
    });
    _listEl.addEventListener('keydown', (e: any) => {
      const li = e.target?.closest?.('li.settings-row');
      // Only the row itself (not a focused inner control) keyboard-activates.
      if (!li || e.target !== li) return;
      // Keyboard can't aim at a line/squiggle, so a focused expandable row
      // toggles on Enter/Space — the accessible equivalent of the fold click.
      const expandable = li.getAttribute('aria-expanded') != null;
      if (e.key === 'Enter') {
        e.preventDefault();
        if (li._onActivate) li._onActivate(e);
        else if (expandable) _toggleExpansion(li.dataset.rowId);
      } else if (e.key === ' ') {
        e.preventDefault();
        if (li._onAux) li._onAux(e);
        else if (li._onActivate) li._onActivate(e);
        else if (expandable) _toggleExpansion(li.dataset.rowId);
      } else if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && li._onAux) {
        e.preventDefault();
        li._onAux(e);
      }
    });

    // ── Per-section accent-line brightening (editor parity) ────────────────
    // 1-to-1 with the editor's nearest-line hover (click-handlers.cm.ts): only
    // the SINGLE section whose line / squiggle is under the cursor brightens —
    // siblings stay faint — driven by per-section vars `--accent-line-sec-<idx>`
    // on #settingsList. The heading text is NOT a hover target (no brightening,
    // no pointer there), so hovering a heading never highlights it. A `pointer`
    // cursor appears on the row only while the cursor is over its line gutter,
    // signalling the fold the same way the editor does.
    let _hoveredSec = -1;
    let _pointerRow: HTMLElement | null = null;
    const _setHoveredSec = (idx: number) => {
      if (idx === _hoveredSec) return;
      if (_hoveredSec >= 0) _listEl.style.removeProperty(`--accent-line-sec-${_hoveredSec}`);
      _hoveredSec = idx;
      if (idx >= 0) {
        _listEl.style.setProperty(`--accent-line-sec-${idx}`, 'var(--accent-line-bright)');
      }
    };
    const _setPointerRow = (el: HTMLElement | null) => {
      if (_pointerRow === el) return;
      if (_pointerRow) _pointerRow.style.cursor = '';
      _pointerRow = el;
      if (el) el.style.cursor = 'pointer';
    };
    const _clearHover = () => {
      _setHoveredSec(-1);
      _setPointerRow(null);
    };
    _listEl.addEventListener(
      'mousemove',
      (e: any) => {
        const t = e.target as HTMLElement;
        // Squiggle (a collapsed group's fold marker): brighten its section
        // directly; it carries its own `cursor: pointer`, so no row cursor.
        const sq = t.closest?.('.settings-fold-squiggle') as HTMLElement | null;
        if (sq) {
          const li = sq.closest('li.settings-row') as HTMLElement | null;
          const idx = li ? Number(li.dataset.foldSec) : NaN;
          if (Number.isFinite(idx) && idx >= 0) {
            _setHoveredSec(idx);
            _setPointerRow(null);
            return;
          }
        }
        const li = t.closest?.('li.settings-row') as HTMLElement | null;
        if (!li || !_listEl.contains(li)) {
          _clearHover();
          return;
        }
        const slot = _foldSlotAtRow(li, e);
        if (slot < 0) {
          _clearHover();
          return;
        }
        const idx = Number((li.dataset.lineSec || '').split(',')[slot]);
        if (!Number.isFinite(idx) || idx < 0) {
          _clearHover();
          return;
        }
        _setHoveredSec(idx);
        _setPointerRow(li);
      },
      { passive: true }
    );
    _listEl.addEventListener('mouseleave', _clearHover);

    _searchEl.addEventListener('input', () => {
      const wasActive = _searchActive();
      _query = _searchEl.value;
      // Crossing the empty↔active boundary flips which groups are force-open,
      // so the row set must be rebuilt. Within an active search the open-state
      // is unchanged — just re-filter the rows already on screen (cheap, and
      // it preserves any rendered sub-content).
      if (_searchActive() !== wasActive) _renderAll();
      else _applyFilter();
    });
    _searchEl.addEventListener('keydown', (/** @type {any} */ e: any) => {
      if (e.key === 'Escape') {
        if (_searchEl.value) {
          _searchEl.value = '';
          _query = '';
          // Re-render to collapse the groups the search force-expanded.
          _renderAll();
        } else {
          SettingsRouter?.close();
        }
      }
    });

    // Clicking the heading closes the panel, mirroring the other panel
    // headers (which cyclePanel via _wirePanelHeader). role="button" and
    // tabindex make it keyboard-activatable too.
    const h2 = settingsContainer.querySelector('h2');
    if (h2) {
      h2.setAttribute('role', 'button');
      h2.setAttribute('tabindex', '0');
      const close = () => SettingsRouter?.close();
      h2.addEventListener('click', close);
      h2.addEventListener('keydown', (e: any) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          close();
        }
      });
    }

    window.addEventListener('settings:route', (ev) => {
      if ((ev as any).detail) _open();
      else _close();
    });

    // Cross-tab refresh — only fire _renderAll when *another tab* mutates
    // the prefs note. Same-tab Settings.set calls do NOT need a re-render
    // because the open colour picker / input is already showing the value
    // the user just chose, and a re-render here would detach native
    // colour-picker dialogs mid-drag (BroadcastChannel doesn't echo to the
    // sending tab, so this listener is safe to rebuild the DOM in).
    subscribeNotesChange((/** @type {any} */ data) => {
      const name = data?.name;
      if (!name?.startsWith('.app_preferences')) return;
      if (_opened) _renderAll();
    });
    window.addEventListener('workspaces:changed', () => {
      clearMembersCache();
      if (_opened) _renderAll();
    });

    // Sync/auth changes from the helpers module — repaint so the Sync
    // row reflects "Signed in as …" without manual refresh.
    if (getSyncHelpers()?.onAuthStateChange) {
      getSyncHelpers().onAuthStateChange(() => {
        handleAuthStateChange();
        if (_opened) _renderAll();
      });
    }
    window.addEventListener('encryption:ready', () => {
      handleEncryptionReady();
      if (_opened) _renderAll();
    });
    // Populate the device-id cache eagerly if encryption is already ready.
    primeDeviceId();

    if (SettingsRouter?.isOpen?.()) _open();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _wire, { once: true });
  } else {
    _wire();
  }

  _SettingsPanel = {
    // Test-only seam for the reconcile unit tests. Exposes the render entry
    // point + state so a test can drive a re-render and assert that unchanged
    // rows are preserved by DOM identity (patched in place) rather than rebuilt.
    // Never called from app code.
    _internals: {
      get listEl() {
        return _listEl;
      },
      sections: _SECTIONS,
      renderAll: () => _renderAll(),
      setQuery: (/** @type {any} */ q: any) => {
        _query = q;
      },
      // Persistence is owned by the _reconcile chokepoint (every mutation path
      // re-renders through it), so these seam mutators don't persist themselves.
      openExpansion: (/** @type {any} */ id: any) => _openExpansions.add(id),
      closeExpansion: (/** @type {any} */ id: any) => _openExpansions.delete(id),
    },
  };
})();

// ES-module export of the panel control surface. The window.SettingsPanel
// back-compat shim is gone — it had no remaining readers. `_SettingsPanel`
// is assigned inside the IIFE above (which runs synchronously at module
// evaluation), so it's populated before this binding is read by importers.
export const SettingsPanel = _SettingsPanel;
